import React from 'react'
import { useTranslation } from 'react-i18next'
import { Clinic as ClinicType } from '@customTypes/Clinic'
import { Button } from '@components/Button/Button'
import useStyles from './styles'
import useCommonStyles from '@screens/OnlineCancellation/commonStyles'
import Page from '@components/Page'
import Header from '@components/Header'
import EventIcon from '@mui/icons-material/Event'
import Footer from '@components/Footer'
import Clinic from '@components/Clinic/Clinic'
import Checkbox from '@components/Checkbox'
import { getFormattedDate, getFormattedTime } from 'src/utils/date'
import { useDateFnsLocale } from '@hooks/useDateFnsLocale'
import { Language } from '@dentalux/ui-library-core/type'
import Spinner from '@components/Spinner'
import { useIsPhoneOnly } from 'src/config/mediaQueries'

type InitialStepProps = {
  clinic: ClinicType | undefined
  onConfirm: () => void
  displayCancellationWarning: boolean
  warningCheckboxChecked: boolean
  toggleWarningCheckboxChecked: () => void
  date: Date
  loading: boolean
}

export const InitialStep = ({
  date,
  clinic,
  onConfirm,
  displayCancellationWarning,
  warningCheckboxChecked,
  toggleWarningCheckboxChecked,
  loading,
}: InitialStepProps) => {
  const { t, i18n } = useTranslation()
  const classes = useStyles()
  const commonClasses = useCommonStyles()
  const locale = useDateFnsLocale()
  const formattedDate = getFormattedDate(date, i18n.language as Language, locale)
  const formattedTime = getFormattedTime(date)
  const isPhone = useIsPhoneOnly()

  const headlineText = t('cancel_appointment_headline')
  const ctaButton = (
    <Button
      className={commonClasses.buttonWrapper}
      text={t('cancel_appointment')}
      onClick={onConfirm}
      disabled={displayCancellationWarning && !warningCheckboxChecked}
    />
  )

  const body = (
    <div className={commonClasses.innerWrapper}>
      <div className={commonClasses.contentWrapper}>
        <div className={classes.iconWithTextWrapper}>
          <EventIcon className={classes.iconStyles} />
          <div className={classes.textStyles}>
            <div>{formattedDate}</div>
            <div className={classes.timeStyles}>{formattedTime}</div>
          </div>
        </div>
        {clinic && <Clinic clinic={clinic} />}
        {displayCancellationWarning && (
          <div className={classes.warningContainer}>
            <div className={classes.warningDescriptionContainer}>{t('cancel_appointment_warning')}</div>
            <div className={classes.warningCheckboxContainer}>
              <Checkbox
                className={classes.warningCheckbox}
                checked={warningCheckboxChecked}
                onChange={toggleWarningCheckboxChecked}
                name="warning-checkbox"
              />
              <div className={classes.warningCheckboxLabel} onClick={toggleWarningCheckboxChecked}>
                {t('cancel_appointment_warning_label')}
              </div>
            </div>
          </div>
        )}
        {!isPhone && ctaButton}
      </div>
    </div>
  )

  return (
    <Page
      header={<Header desktopHeadingText={headlineText} mobileHeadingText={headlineText} />}
      body={loading ? <Spinner center size="large" /> : body}
      footer={loading || !isPhone ? undefined : <Footer button={ctaButton} />}
    />
  )
}
